import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
// V3 components
import ReactMarkdown from 'react-markdown';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { isMobile, range } from '/@/utils';
import Dots from '/@/v3/components/dots';
import { DotsReference } from '/@/v3/components/dots-reference';
import { RangeReference } from '/@/v3/components/range-reference';
import styles from './styles.module.css';

@autobind
export default class Metrics extends Component {
    static propTypes = {
        dataKey: PropTypes.string.isRequired,
        products: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                product: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
                metrics: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.object.isRequired,
                        value: PropTypes.number.isRequired,
                        referenceCodename: PropTypes.string.isRequired,
                    })
                ),
                features: PropTypes.arrayOf(
                    PropTypes.shape({
                        name: PropTypes.object.isRequired,
                        value: PropTypes.object.isRequired,
                    })
                ),
            })
        ).isRequired,
        dots: PropTypes.bool,
        brand: PropTypes.object,
        highlight: PropTypes.bool,
        country: PropTypes.object,
        catalog: PropTypes.bool,
    };

    groupProducts(products) {
        const productGroups = products.reduce((groups, product) => {
            product[this.props.dataKey].forEach(({ name, value, order }) => {
                name = _(name);
                (groups[name] = groups[name] || []).push({
                    id: product.id,
                    name: product.product.name,
                    ranking: product.ranking,
                    value,
                    order,
                });
            });
            return groups;
        }, {});
        return Object.entries(productGroups)
            .sort((a, b) => a[1]?.[0]?.order - b[1]?.[0]?.order)
            .map(([name, products]) => ({ name, products }));
    }

    getGroupComponent({ name, products, catalog }) {
        return (
            <div key={name} className={classNames(styles.group, catalog ? styles.catalog : '')}>
                <div className={styles.title}>{name}</div>
                <div className={styles.list}>
                    {products.map((product) => (
                        <div
                            key={product.id}
                            className={classNames(styles.row, this.props.catalog ? styles.catalogMetricRow : '')}
                        >
                            <div
                                className={classNames(styles.product, this.props.catalog ? styles.catalogProduct : '')}
                            >
                                {product.name}
                            </div>
                            {this.renderValue(product.value)}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    getTableComponent({ columns, rows, data }) {
        return (
            <table className={styles.table} cellSpacing={0}>
                <thead>
                    <tr>
                        <th />
                        {columns.map((title) => (
                            <th key={title}>{title}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ name, isDisease }) => {
                        return (
                            <tr key={name}>
                                <td className={classNames(isDisease ? styles.disease : '')}>{name}</td>
                                {columns.map((column) => (
                                    <td key={name + Math.random()} className={styles.dataTd}>
                                        {!!data[name] && !!data[name][column] && this.renderValue(data[name][column])}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderValue(value) {
        if (this.props.dots) {
            return (
                <Dots
                    single
                    max={this.props.brand.metricsMaxValue}
                    value={value}
                    theme={this.props.brand.theme}
                    showValues={this.props.brand.showMetricsValues}
                    monochrome={this.props.brand.showMetricsUsingBrandTheme}
                    rootCenter
                />
            );
        }
        return (
            <div className={classNames(styles.value, this.props.catalog ? styles.catalogMetricValue : '')}>
                <ReactMarkdown>{_(value)}</ReactMarkdown>
            </div>
        );
    }

    isDisease(groups, products) {
        const metrics = {};
        products.forEach((product) => {
            product.metrics.forEach((metric) => {
                if (!metrics[metric.name.es]) metrics[metric.name.es] = metric.isDisease;
            });
        });
        return groups.map((group) => {
            return { ...group, isDisease: metrics[group.name] };
        });
    }

    render() {
        const products = this.props.products.filter(
            (product) => product[this.props.dataKey] && product[this.props.dataKey].length > 0
        );

        if (products.length === 0) return null;

        let groups = this.groupProducts(products);
        if (this.props.dataKey === 'metrics') groups = this.isDisease(groups, products);
        const single = products.length === 1;

        const Group = this.getGroupComponent;
        const Table = this.getTableComponent;
        const mobile = isMobile();

        const referenceCodenames = [];
        if (this.props.dots) {
            products.forEach((product) =>
                product.metrics.forEach(
                    (metric) =>
                        metric.referenceCodename !== 'default' &&
                        !referenceCodenames.includes(metric.referenceCodename) &&
                        referenceCodenames.push(metric.referenceCodename)
                )
            );
        }
        const useDefaultReference = referenceCodenames.length === 0;

        return (
            <div className={`${styles.root} mp-mask`}>
                {this.props.dots && useDefaultReference && (
                    <DotsReference brand={this.props.brand} country={this.props.country} />
                )}
                {mobile ? (
                    <div className={styles.groups}>
                        {groups.map((group) => (
                            <Group
                                key={group.name}
                                name={group.name}
                                products={group.products}
                                catalog={this.props.catalog}
                            />
                        ))}
                    </div>
                ) : (
                    <div
                        className={classNames(styles.table, {
                            [styles.single]: single,
                            [styles.highlight]: this.props.highlight && !single,
                        })}
                    >
                        <Table
                            columns={products.map((x) => x.product.name)}
                            rows={groups.map((x) => {
                                return { name: x.name, isDisease: x.isDisease };
                            })}
                            data={groups.reduce(
                                (acc, x) => ({
                                    ...acc,
                                    [x.name]: x.products.reduce(
                                        (acc, { name, value }) => ({
                                            ...acc,
                                            [name]: value,
                                        }),
                                        {}
                                    ),
                                }),
                                {}
                            )}
                        />
                    </div>
                )}
                {this.props.dots && !useDefaultReference && (
                    <RangeReference referenceCodenames={referenceCodenames} theme={this.props.brand.theme} />
                )}
            </div>
        );
    }
}

const HEADERS_TABLE = [
    { id: 1, name: 'Densidad final de la planta por hectárea', property: 'density' },
    { id: 2, name: 'Objetivo', property: 'objective' },
    { id: 3, name: 'Época de plantio', property: 'plantingDate' },
    { id: 4, name: 'Ambiente de producción', property: 'productionEnvironment' },
    { id: 5, name: 'Nível de Acamamento', property: 'acamamentoLevel' },
    { id: 6, name: 'Rendimiento medio de USB', property: 'ubsYield' },
    { id: 7, name: 'Paneira Media', property: 'averageSieve' },
    { id: 8, name: 'Lignina (%)', property: 'lignin' },
];
@autobind
export class Multipliers extends Component {
    static propTypes = {
        highlight: PropTypes.bool,
        user: PropTypes.object,
    };

    renderValue(value) {
        return (
            <div className={classNames(styles.value, this.props.catalog ? styles.catalogMetricValue : '')}>
                {_(value)}
            </div>
        );
    }

    getTableComponent(profiles) {
        const single = profiles.length === 1;
        const { user, highlight } = this.props;

        return (
            <table
                className={classNames(styles.table, {
                    [styles.singleFull]: single,
                    [styles.highlight]: highlight && !single,
                })}
                cellSpacing={0}
            >
                <thead>
                    <tr>
                        <th />
                        {profiles.map((profile) => {
                            return (
                                <th key={profile.productName}>
                                    <div className={styles.textRelative}>
                                        {profile.productName}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Densidad final de la planta por hectárea')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.density)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Objetivo')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.objective)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Temporada de siembra')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.plantingDate)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Ambiente de producción')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.productionEnvironment)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Nivel de acamamento')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.acamamentoLevel)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Rendimiento medio de USB')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.ubsYield)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Paneira Media')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.averageSieve)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                    <tr>
                        <td>
                            <div className={styles.textRelative}>
                                {_('Lignina (%)')}
                                <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                            </div>
                        </td>
                        {profiles.map((profile) => {
                            return (
                                <td key={profile.key} className={styles.dataTd}>
                                    <div className={styles.textRelative}>
                                        {this.renderValue(profile.lignin)}
                                        <div className={styles.watermark}>{range(4).map(() => `${user.email} `)}</div>
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        );
    }

    groupProducts(products, headerTable) {
        const data = headerTable.map((item) => {
            const productGroups = products.reduce((groups, product) => {
                const name = _(item.name);
                (groups[name] = groups[name] || []).push({
                    id: product.id,
                    value: product[item.property],
                    productName: product.productName,
                });
                return groups;
            }, {});
            return Object.entries(productGroups)
                .sort((a, b) => a[1]?.[0]?.order - b[1]?.[0]?.order)
                .map(([name, products]) => ({ name, products }));
        });
        return data;
    }

    getGroupComponent({ name, products, catalog }) {
        return (
            <div key={name} className={classNames(styles.group, catalog ? styles.catalog : '')}>
                <div className={styles.title}>{_(name)}</div>
                <div className={styles.list}>
                    {products.map((product) => (
                        <div
                            key={product.id}
                            className={classNames(styles.row, catalog ? styles.catalogMetricRow : '')}
                        >
                            <div className={styles.product}>{product.productName}</div>
                            <div>{product.value}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    getTableMobileComponent(profiles) {
        const groups = this.groupProducts(profiles, HEADERS_TABLE);
        if (!groups) return null;
        const Group = this.getGroupComponent;
        return (
            <div className={styles.groups}>
                {groups.map((group) => {
                    return (
                        <Group
                            key={(group[0] && group?.[0]?.name) || ''}
                            name={(group[0] && group?.[0]?.name) || ''}
                            products={(group[0] && group?.[0]?.products) || []}
                            catalog={this.props.catalog}
                        />
                    );
                })}
            </div>
        );
    }

    render() {
        let profiles = this.props.profiles;
        profiles = profiles.map((profile) => {
            const objectives = settings.choices.objectives;
            const acamamentoLevels = settings.choices.acamamentoLevels;
            const productionEnvironments = settings.choices.productionEnvironments;

            let objective = objectives.find(({ identifier }) => profile.objective == identifier);
            objective = objective ? objective.label : profile.objective;

            let acamamentoLevel = acamamentoLevels.find(({ identifier }) => profile.acamamentoLevel == identifier);
            acamamentoLevel = acamamentoLevel ? acamamentoLevel.label : profile.acamamento;

            let productionEnvironment = productionEnvironments.find(
                ({ identifier }) => profile.productionEnvironment == identifier
            );
            productionEnvironment = productionEnvironment ? productionEnvironment.label : profile.production;

            return {
                ...profile,
                objective,
                acamamentoLevel,
                productionEnvironment,
            };
        });
        const mobile = isMobile();
        const TableDesktop = this.getTableComponent(profiles);
        const TableMobile = this.getTableMobileComponent(profiles);

        return <Fragment>{mobile ? TableMobile : TableDesktop}</Fragment>;
    }
}
